<script setup lang="ts">
import { Plus, EditPencil, Trash, LogIn } from "@iconoir/vue";
import ListPage from "~/components/templates/ListPage.vue";
import ListEmpty from "~/components/templates/ListEmpty.vue";
import { useList } from "~/composables/use-list";
import { useCrud } from "~/composables/use-crud";
import { useFormat } from "~/composables/use-format";
import type { User } from "@artech-global/types";
import { AdminPermission } from "~~/schemas/admin-role";
import MemberFormModal from "./MemberFormModal.vue";

defineOptions({ name: "MemberList" });

const props = defineProps<{
  workspaceID: string;
}>();

const { t } = useI18n();
const { d } = useFormat();
const { can } = useAccess();
const { $api } = useNuxtApp();
const toast = useToast();
const alert = useAlert();
const config = useRuntimeConfig();

const defaultFilter = {
  workspaceID: props.workspaceID,
  keyword: "",
  status: "all",
  page: 1,
  pageSize: 50,
};

const {
  firstLoading,
  listLoading,
  load,
  itemCount,
  pageCount,
  items,
  filter,
  isEmpty,
  isSearchEmpty,
  search,
  searchDebounced,
} = useList<User, typeof defaultFilter>({
  endpoint: "/users",
  defaultFilter,
});

const { del, errorMsg } = useCrud<User>({
  endpoint: "/users",
});

const formRef = ref<InstanceType<typeof MemberFormModal> | null>(null);
const signInLoadingId = ref<string | null>(null);

const statusOptions = computed(() => [
  { value: "all", label: t("workspace.statusAll") },
  { value: "deleted", label: t("workspace.statusDeleted") },
]);

function openCreate() {
  formRef.value?.open();
}

function openEdit(row: User) {
  formRef.value?.open(row.id as string);
}

async function confirmDelete(row: User) {
  const ok = await alert.negative({
    title: t("workspace.member.deleteConfirmTitle"),
    description: t("workspace.member.deleteConfirmBody", {
      name: row.name || row.email || "",
    }),
    confirmLabel: t("action.delete"),
    cancelLabel: t("action.cancel"),
  });
  if (!ok) return;

  try {
    await del(row.id as string);
    toast.success({ title: t("message.deleteSuccess") });
    const nextItemCount = Math.max(0, itemCount.value - 1);
    const nextPageCount = Math.max(
      1,
      Math.ceil(nextItemCount / filter.pageSize),
    );
    if (Number(filter.page) > nextPageCount) {
      filter.page = nextPageCount;
    }
    await load();
  } catch {
    if (errorMsg.value) toast.error({ title: errorMsg.value });
  }
}

async function signIn(row: User) {
  const clientAppUrl = (config.public.clientAppUrl as string) || "";
  if (!clientAppUrl) {
    toast.error({ title: t("workspace.member.clientAppUrlMissing") });
    return;
  }

  const ok = await alert.positive({
    title: t("workspace.member.signInConfirmTitle", {
      name: row.name || row.email || "",
    }),
    description: t("workspace.member.signInConfirmBody"),
    confirmLabel: t("workspace.member.signIn"),
    cancelLabel: t("action.cancel"),
  });
  if (!ok) return;

  signInLoadingId.value = (row.id as string) || null;
  try {
    const resp: any = await $api(`/users/token/${row.id}`);
    const token = resp?.token;
    if (!token) {
      toast.error({ title: t("workspace.member.signInError") });
      return;
    }
    const url = `${clientAppUrl.replace(/\/$/, "")}/use-token/${token}?workspaceID=${encodeURIComponent(props.workspaceID)}`;
    const win = window.open(url, "_blank");
    if (!win) {
      toast.error({
        title: t("workspace.member.signInBlockedTitle"),
        description: t("workspace.member.signInBlockedBody"),
      });
      return;
    }
    toast.success({ title: t("workspace.member.signInOpening") });
  } catch (e: any) {
    toast.error({
      title: e?.data?.message || t("workspace.member.signInError"),
    });
  } finally {
    signInLoadingId.value = null;
  }
}

async function onSaved() {
  await load();
}

onMounted(async () => {
  try {
    await load();
  } catch (e: any) {
    toast.error({
      title: e?.data?.message || t("workspace.member.loadError"),
    });
  }
});

watch(
  () => props.workspaceID,
  (val) => {
    filter.workspaceID = val;
    search();
  },
);

watch(
  () => filter.keyword,
  () => {
    searchDebounced();
  },
);

watch(
  () => filter.status,
  () => {
    search();
  },
);

watch(
  () => filter.page,
  () => {
    load();
  },
);
</script>

<template>
  <ListPage
    :first-loading="firstLoading"
    :is-empty="isEmpty"
    :is-search-empty="isSearchEmpty"
  >
    <template #status>
        {{
          listLoading
            ? t("ui.loading")
            : `${itemCount} ${t("workspace.member.itemsSuffix")}`
        }}
    </template>

    <template #actions>
      <Button v-if="can(AdminPermission.UsersWrite)" variant="primary" size="small" @click="openCreate">
        <Plus class="icon" />
        {{ t("workspace.member.add") }}
      </Button>
    </template>

    <template #empty>
      <ListEmpty
        :title="t('workspace.member.emptyTitle')"
        :subtitle="t('workspace.member.emptySubtitle')"
      >
        <template #action>
          <Button v-if="can(AdminPermission.UsersWrite)" variant="primary" size="small" @click="openCreate">
            <Plus class="icon" />
            {{ t("workspace.member.add") }}
          </Button>
        </template>
      </ListEmpty>
    </template>

    <template #search-empty>
      <ListEmpty
        :title="t('workspace.member.emptySearchTitle')"
        :subtitle="t('workspace.member.emptySearchSubtitle')"
      />
    </template>

    <template #filter-left>
      <FormSelect
        v-model="filter.status"
        :options="statusOptions"
        size="sm"
        class="w-full md:w-1/2"
      />
    </template>

    <template #filter-right>
      <FormSearch
        v-model="filter.keyword"
        :placeholder="t('field.search')"
        size="sm"
        class="w-full md:w-1/2"
      />
    </template>

    <template #default>
      <DataTable
        :columns="[
          { key: 'user', label: t('field.fullName'), class: 'md:min-w-[240px]' },
          { key: 'createTime', label: t('field.createTime'), class: 'min-w-[160px] md:table-cell' },
          { key: 'actions', align: 'right', label: t('ui.action'), class: 'min-w-[80px]' },
        ]"
        :items="items"
      >
        <template #cell-user="{ row }">
          <div class="flex items-center gap-small">
            <Avatar
              :src="row.featuredAsset?.url"
              :initials="(row.name || row.email || '').substring(0, 2).toUpperCase()"
              size="small"
              shape="rounded"
            />
            <div class="flex flex-col min-w-0">
              <span class="font-semibold">{{ row.name || "—" }}</span>
              <span class="text-body-small-tertiary content-neutral-medium">
                {{ row.email }}
              </span>
            </div>
          </div>
        </template>

        <template #cell-createTime="{ row }">
          {{ d(row.createTime, 'datetime') }}
        </template>

        <template #actions="{ row }">
          <div class="flex items-center justify-end gap-xsmall">
            <ButtonIcon
              v-if="!row.deleteTime && can(AdminPermission.UsersSignIn)"
              variant="neutral-flat"
              size="small"
              :title="t('workspace.member.signIn')"
              :aria-label="t('workspace.member.signIn')"
              :disabled="signInLoadingId === row.id"
              @click.stop="signIn(row)"
            >
              <ProgressCircle v-if="signInLoadingId === row.id" size="small" />
              <LogIn v-else class="icon" />
            </ButtonIcon>

            <ButtonIcon
              v-if="!row.deleteTime && can(AdminPermission.UsersWrite)"
              variant="neutral-flat"
              size="small"
              :title="t('action.edit')"
              :aria-label="t('action.edit')"
              @click.stop="openEdit(row)"
            >
              <EditPencil class="icon" />
            </ButtonIcon>

            <ButtonIcon
              v-if="!row.deleteTime && can(AdminPermission.UsersDelete)"
              variant="neutral-flat"
              size="small"
              :title="t('action.delete')"
              :aria-label="t('action.delete')"
              @click.stop="confirmDelete(row)"
            >
              <Trash class="icon" />
            </ButtonIcon>
          </div>
        </template>
      </DataTable>

      <Pagination
        v-if="pageCount > 1"
        v-model:page="filter.page"
        :count="itemCount"
        :page-size="filter.pageSize"
        class="mt-medium"
      />
    </template>
  </ListPage>

  <MemberFormModal
    ref="formRef"
    :workspace-i-d="workspaceID"
    @saved="onSaved"
  />
</template>
